import { lighten } from "@wessberg/color";
import Head from "next/head";
import { RichText } from "prismic-reactjs";
import { useContext, useEffect, useState } from "react";
import { useSelector } from "react-redux";
import styled from "styled-components";
import BigCopy from "~/components/BigCopy";
import Block, { BlockContent, BlockTopSpacer, BlockWrapper } from "~/components/Block";
import BreadCrumbs from "~/components/BreadCrumbs";
import ButtonArrow from "~/components/ButtonArrow";
import Divider from "~/components/Divider";
import HorizontalRule from "~/components/HorizontalRule";
import LinkButton from "~/components/LinkButton";
import ProductGrid from "~/components/ProductGrid";
import { SimpleQuestionList } from "~/components/QuestionList";
import { getProductsByKeys } from "~/external/commercetools";
import { linkResolver } from "~/external/prismic";
import { prismic } from "~/external/v2/prismic";
import { CommonPageProps, QuestionDocument } from "~/external/v2/types";
import { CtpCtx } from "~/lib/ctpApollo";
import theme, { smallerThan } from "~/styles/theme";
import Error from "~/views/Error";

const QuestionContentOuter = styled(BlockContent)<any>`
  min-height: 33vh;
  /*padding: 0 10%;*/
  /*padding: 0;*/

  ${BigCopy} {
    h1 {
      /*text-align: center;*/
      font-size: 1.5em;
      padding: 0;
      margin: 0;
    }
  }

  /*${BigCopy}+p {
    color: ${(p) => lighten(p.theme.colors.dark, 15)};
  }

  ${HorizontalRule} {
    margin: 0 45%;
    background-color: ${(p) => p.theme.colors.greyLight};
  }*/

  ${(p) => smallerThan(p.theme.breakpoints.small)} {
    /*padding: 0;
    margin: 0;*/

    h1 {
      font-size: 1.2em;
    }
  }
`;

const QuestionContentBody = styled(BlockContent)<any>`
  min-height: 20vh;
  padding: ${(p) => p.theme.sizes.vSpacing}rem 50% ${(p) => p.theme.sizes.vSpacing}rem 0;
  line-height: 2;

  ${(p) => smallerThan(p.theme.breakpoints.smallDesktop)} {
    padding: ${(p) => p.theme.sizes.vSpacing}rem 35% ${(p) => p.theme.sizes.vSpacing}rem
      0;
  }

  ${(p) => smallerThan(p.theme.breakpoints.ipadLandscape)} {
    padding: ${(p) => p.theme.sizes.vSpacingSmall}rem 25%
      ${(p) => p.theme.sizes.vSpacingSmall}rem 0;
  }
  ${(p) => smallerThan(p.theme.breakpoints.small)} {
    padding: ${(p) => p.theme.sizes.vSpacingSmall}rem 0;
  }

  .youtube-embed {
    position: relative;
    padding-bottom: 56.25%;
    height: 0;
    overflow: hidden;
    max-width: 100%;

    iframe {
      position: absolute;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
    }
  }
`;

const QuestionContentRelated = styled(BlockContent)<any>`
  /*padding: 0 2%;
  margin: 2% 4%;*/

  padding-top: ${(p) => p.theme.sizes.vSpacing * 10}rem;

  ${BigCopy} h5, ${BigCopy} h6 {
    margin-bottom: 1%;
    margin-top: 0;
  }

  /*${(p) => smallerThan(p.theme.breakpoints.small)} {
    padding: 0;
    margin: 0;
  }*/

  ${BlockWrapper} > div {
    padding: 0;
    & > ${BlockContent} {
      padding: 0 0.2rem;
    }
  }
`;

const QuestionContentEnd = styled(BlockContent)<any>`
  min-height: 40vh;
  padding: 0 4%;
  margin: 4%;
  background-color: ${theme.colors.greyLighter};
  h3 {
    text-align: center;
  }

  ${(p) => smallerThan(p.theme.breakpoints.small)} {
    padding: 0;
    margin: 0;
    h3 {
      font-size: smaller;
      /*min-height: 30vh;*/
    }
  }
  div:last-child {
    text-align: center;
    margin-top: 2em;

    a {
      color: ${(p) => p.theme.colors.dark};
      text-decoration: none;
      &:hover {
        text-decoration: underline;
      }
    }
  }
`;

// htmlSerializer to output Youtube embed in full width
const htmlSerializer = (type, element, content, children, key) => {
  // Check if the element is an embed and if the provider is YouTube
  if (type === "embed" && element.oembed.provider_name === "YouTube") {
    // Add a custom class to the embed element
    return (
      <div
        className="youtube-embed"
        key={key}
        dangerouslySetInnerHTML={{ __html: element.oembed.html }}
      />
    );
  }

  // Return the default HTML for other elements
  return null;
};

interface QuestionPageProps extends CommonPageProps {
  question: QuestionDocument;
}

function QuestionPage(props: QuestionPageProps) {
  if (props.error) return <Error {...props.error} />;

  const { question } = props;

  const ctpCtx = useContext(CtpCtx);
  // @ts-ignore
  const countryData = useSelector((state) => state.countryData);

  const [products, setProducts] = useState([]);

  useEffect(() => {
    if (
      !question.content.related_products ||
      question.content.related_products.length === 0
    )
      return;

    const getItems = async () => {
      const products = await getProductsByKeys(
        ctpCtx,
        question.content.related_products.map((item) => item.related_product.uid),
        countryData
      );
      setProducts(
        products.map((product) => ({
          product: { ...product, ...product.masterData.current },
        }))
      );
    };

    getItems();
  }, []);

  const breadcrumbs = [["Help", "/help"]];
  if (question.content.category) {
    console.log(question.content.category);
    breadcrumbs.push([
      question.content.category.data.title,
      // TODO: Regex replace - replaceAll is not available without polyfill
      // - or change to document type QuestionCategory
      `/help/${question.content.category.uid}`,
    ]);
  }

  return (
    <>
      <Head>
        <title>{question.content.title} | Help | AIAIAI</title>
        {question.content.brief && (
          <meta name="description" content={question.content.brief} />
        )}
        {/* TODO: Consider using text from first paragraph of body (rich text) */}
      </Head>
      <Block>
        <BlockTopSpacer />
        <QuestionContentOuter>
          <BreadCrumbs linkAll breadcrumbs={breadcrumbs} />
          <Divider opacity={0.26} />
          <BigCopy fontWeight={800}>
            <h3 style={{ margin: 0 }}>{question.content.title}</h3>
          </BigCopy>
          <QuestionContentBody>
            <RichText
              render={question.content.body}
              linkResolver={linkResolver}
              htmlSerializer={htmlSerializer}
            />
          </QuestionContentBody>
        </QuestionContentOuter>
      </Block>

      {question.content.related_questions &&
        question.content.related_questions.length > 0 && (
          <Block>
            <QuestionContentRelated>
              <Divider />
              <BigCopy fontWeight={800}>
                <h3 style={{ margin: 0 }}>Related questions</h3>
              </BigCopy>
              <SimpleQuestionList>
                {question.content.related_questions.map((item, i) => (
                  <li key={i}>
                    <a
                      href={`/help/${
                        item.related_question.data.category?.uid ||
                        question.content.category.uid
                      }/${item.related_question.uid}`}
                    >
                      {item.related_question.data.title}
                    </a>
                  </li>
                ))}
              </SimpleQuestionList>
            </QuestionContentRelated>
            <BlockTopSpacer spacing="topBar" />
          </Block>
        )}

      {products.length > 0 && (
        <Block>
          <QuestionContentRelated>
            <Divider />
            <BigCopy fontWeight={800}>
              <h3 style={{ margin: 0 }}>Related products</h3>
            </BigCopy>
            <ProductGrid
              cards={products}
              columnsOnMobile={2}
              skipCrossSells
              buttonType="none"
              showText={false}
              showPrice={false}
            />
          </QuestionContentRelated>
          <BlockTopSpacer spacing="topBar" />
        </Block>
      )}

      <Block>
        <BlockContent>
          <BigCopy fontWeight={800}>
            <h3>Can’t find what you’re looking for?</h3>
          </BigCopy>
        </BlockContent>
        <BlockContent>
          <LinkButton
            buttonComponent={ButtonArrow}
            buttonText="Contact us"
            href="/help/contact"
          />
        </BlockContent>
        <BlockTopSpacer spacing="topBar" />
      </Block>
    </>
  );
}

export const getServerSideProps = async ({ query, res }) => {
  const document = await prismic.question.get(query.questionUid);

  // console.log('document =>', JSON.stringify(document, null, 2));

  /* const results = await prismic.question.fullText('tma-2');

  console.log('results =>', JSON.stringify(results, null, 2)); */

  /* console.log(
    'related_products =>',
    JSON.stringify(question.data.related_products, null, 2)
  );
  console.log(
    'related_questions =>',
    JSON.stringify(question.data.related_questions, null, 2)
  ); */

  // const documents = await prismic.question.query.byCategories([question.content.category]);

  if (!document) {
    res.statusCode = 404;
    // context.res.end();
    return {
      props: {
        error: {
          statusCode: 404,
          message: "Document not found",
        },
      },
    };
  }

  const similar = await prismic.question.similar(document.id, 30);
  document.content.related_questions = document.content.related_questions || [];

  // console.log('SIMILAR', JSON.stringify(similar, null, 2));

  for (const result of similar) {
    if (document.content.related_questions.length == 5) break;
    if (
      !document.content.related_questions.some(
        (item) => item.related_question.id == result.id
      ) &&
      !!result.content.category?.id // Filter out questions without category
    ) {
      document.content.related_questions.push({
        // @ts-ignore:next
        related_question: {
          id: result.id,
          uid: result.uid,
          tags: result.tags,
          type: result.type,
          data: {
            title: result.content.title,
            category: result.content.category,
          },
        },
      });
    }
  }

  // console.log('RELATED', JSON.stringify(document.content.related_questions, null, 2));

  return {
    props: {
      question: document,
    },
  };
};

export default QuestionPage;
